/**
 * A pergunta da adoção da Pessoa solta de outro nome (13.14a, DW-388).
 *
 * Até a 13.14a o cadastro de Pessoa com o telefone de uma Pessoa solta a adotava sempre — e o nome
 * que o recebedor tinha dado a ela no painel (a "Carla" da recepção do cliente) era sobrescrito pelo
 * do formulário. A resposta do Gustavo: só se adota a solta ainda **sem nome**; com nome diferente, a
 * tela pergunta "Este número é da Carla. O Marcos é a mesma pessoa?". Quem pergunta é o serviço
 * (`409` `contato_com_outro_nome`, a mensagem verbatim, com o `id` e o nome da solta); aqui ficam os
 * dois botões e a resposta que o próximo envio leva:
 *
 * - **"Sim, é a mesma pessoa"** → `adocao: adotar` com o `contato_id` perguntado (o serviço só adota
 *   **aquela** solta; se ela mudou, pergunta de novo);
 * - **"Não, é outra pessoa"** → `adocao: criar_nova` (nasce uma Pessoa nova; a solta fica).
 *
 * Usada pela ficha nova (cada linha, `PessoasDaFichaNova`), pela ficha que existe
 * (`ResponsaveisDaEmpresa`) e pelo "+ Pessoa" da tela Pessoas — com o estilo de
 * `responsaveis-da-empresa.css` nos três.
 */

import type { ContatoPerguntado } from './api'
import './responsaveis-da-empresa.css'

export const SIM_A_MESMA_PESSOA = 'Sim, é a mesma pessoa'
export const NAO_OUTRA_PESSOA = 'Não, é outra pessoa'

/** O que a linha (ou o formulário) leva no próximo envio. */
export type RespostaDaAdocao = { adocao: 'adotar'; contatoId: string } | { adocao: 'criar_nova' }

/** A nota da linha respondida — o que vai acontecer no próximo envio. */
export function notaDaResposta(resposta: RespostaDaAdocao): string {
  return resposta.adocao === 'adotar'
    ? 'Será vinculada ao contato do WhatsApp com este número.'
    : 'Será cadastrada como pessoa nova; o contato do WhatsApp fica como está.'
}

/** O corpo da resposta, como a rota a recebe (`adocao` e `contato_id`). */
export function corpoDaResposta(
  resposta: RespostaDaAdocao | null,
): { adocao?: 'adotar' | 'criar_nova'; contato_id?: string } {
  if (resposta === null) {
    return {}
  }
  return resposta.adocao === 'adotar'
    ? { adocao: 'adotar', contato_id: resposta.contatoId }
    : { adocao: 'criar_nova' }
}

export function PerguntaDaAdocao({
  contato,
  desabilitada = false,
  aoResponder,
}: {
  contato: ContatoPerguntado
  desabilitada?: boolean
  aoResponder: (resposta: RespostaDaAdocao) => void
}) {
  return (
    <div className="responsaveis__pergunta" role="group" aria-label="A mesma pessoa?">
      <button
        type="button"
        className="responsaveis__acao"
        disabled={desabilitada}
        onClick={() => {
          aoResponder({ adocao: 'adotar', contatoId: contato.id })
        }}
      >
        {SIM_A_MESMA_PESSOA}
      </button>
      <button
        type="button"
        className="responsaveis__acao"
        disabled={desabilitada}
        onClick={() => {
          aoResponder({ adocao: 'criar_nova' })
        }}
      >
        {NAO_OUTRA_PESSOA}
      </button>
    </div>
  )
}
